import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

import COLORS from 'pared/constants/colors'

interface IProps {
  feedback: string
  characterLimit?: number
  employeeName?: string
  performanceRating?: string
  reviewerName?: string
  reviewDate?: string
}

function FeedbackMessage({
  feedback,
  characterLimit = 150,
  employeeName,
  performanceRating,
  reviewerName,
  reviewDate,
}: IProps) {
  const [showFullModal, setShowFullModal] = useState(false)

  const shouldTruncate = feedback.length > characterLimit
  const displayText = shouldTruncate
    ? feedback.substring(0, characterLimit) + '...'
    : feedback

  const handleSeeMore = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('See more clicked for feedback:', feedback.substring(0, 50))
    setShowFullModal(true)
  }

  const handleCloseModal = () => {
    setShowFullModal(false)
  }

  const modalContent = showFullModal ? (
    <ModalOverlay onClick={handleCloseModal}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Feedback Details</ModalTitle>
          <CloseButton onClick={handleCloseModal}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          {employeeName && (
            <InfoSection>
              <InfoLabel>Employee:</InfoLabel>
              <InfoValue>{employeeName}</InfoValue>
            </InfoSection>
          )}
          {performanceRating && (
            <InfoSection>
              <InfoLabel>Performance Rating:</InfoLabel>
              <InfoValue>{performanceRating}</InfoValue>
            </InfoSection>
          )}
          {reviewerName && (
            <InfoSection>
              <InfoLabel>Reviewed By:</InfoLabel>
              <InfoValue>{reviewerName}</InfoValue>
            </InfoSection>
          )}
          {reviewDate && (
            <InfoSection>
              <InfoLabel>Review Date:</InfoLabel>
              <InfoValue>{reviewDate}</InfoValue>
            </InfoSection>
          )}
          <FeedbackSection>
            <FeedbackLabel>Feedback:</FeedbackLabel>
            <FeedbackText>{feedback}</FeedbackText>
          </FeedbackSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  ) : null

  return (
    <>
      <span>
        {displayText}
        {shouldTruncate && (
          <SeeMoreLink onClick={handleSeeMore}>see more</SeeMoreLink>
        )}
      </span>

      {modalContent && ReactDOM.createPortal(modalContent, document.body)}
    </>
  )
}

const SeeMoreLink = styled.button`
  color: #07a4c7;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 5px;
  font-family: Lexend-Regular;
  font-size: 12px;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: none;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  position: relative;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid ${COLORS.Chalkboard};
`

const ModalTitle = styled.h3`
  margin: 0;
  font-family: Lexend-SemiBold;
  font-size: 18px;
  color: ${COLORS.Black};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: ${COLORS.Chalkboard};
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${COLORS.Black};
  }
`

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  font-family: Lexend-Regular;
  font-size: 14px;
  line-height: 1.6;
  color: ${COLORS.Black};
`

const InfoSection = styled.div`
  display: flex;
  margin-bottom: 15px;
`

const InfoLabel = styled.span`
  font-family: Lexend-SemiBold;
  font-weight: 600;
  margin-right: 10px;
  min-width: 150px;
  color: ${COLORS.Black};
`

const InfoValue = styled.span`
  font-family: Lexend-Regular;
  color: ${COLORS.Black};
`

const FeedbackSection = styled.div`
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid ${COLORS.Chalkboard}20;
`

const FeedbackLabel = styled.div`
  font-family: Lexend-SemiBold;
  font-weight: 600;
  margin-bottom: 10px;
  color: ${COLORS.Black};
`

const FeedbackText = styled.div`
  font-family: Lexend-Regular;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: ${COLORS.Black};
`

export default FeedbackMessage
